import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';
import { ArrowLeft, DollarSign, Search, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAdminEmail = () => {
  return localStorage.getItem('admin_email') || localStorage.getItem('adminEmail') || 'info@trucknsendit.com';
};

const AdminTransactions = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/admin/transactions?admin_email=${getAdminEmail()}`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = transactions.filter(t => {
    const matchesSearch = searchTerm === '' ||
      (t.shipper_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.carrier_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.load_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.stripe_session_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.metadata?.user_email || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const type = t.metadata?.type || (t.load_id === 'subscription' ? 'subscription' : 'load');
    const matchesType = filterType === 'all' || type === filterType;
    
    return matchesSearch && matchesType;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'initiated': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'refunded': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (t) => {
    if (t.metadata?.type === 'subscription') return 'Subscription';
    if (t.metadata?.type === 'adhoc_payment') return 'Ad-hoc Job';
    if (t.metadata?.type === 'shipper_payment') return 'Shipper Payment';
    if (t.load_id === 'subscription') return 'Subscription';
    return 'Load Payment';
  };

  const getUser = (t) => {
    return t.metadata?.user_email || t.shipper_email || t.carrier_email || 'Unknown';
  };

  const getReference = (t) => {
    if (t.load_id === 'subscription') {
      return `${(t.metadata?.subscription_plan || 'plan').toUpperCase()} subscription`;
    }
    return t.load_id ? t.load_id.slice(0, 8) : '—';
  };

  const exportCSV = () => {
    const headers = ['Date', 'User', 'Type', 'Amount', 'Status', 'Reference', 'Stripe ID'];
    const rows = filtered.map(t => [
      new Date(t.created_at).toLocaleString('en-AU'),
      getUser(t),
      getTypeLabel(t),
      `$${(t.amount || 0).toFixed(2)}`,
      t.payment_status,
      getReference(t),
      t.stripe_session_id || ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const totalRevenue = filtered.filter(t => t.payment_status === 'paid').reduce((sum, t) => sum + (t.amount || 0), 0);

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate('/admin')} className="text-sm">
                <ArrowLeft className="w-4 h-4 mr-1" /> Admin
              </Button>
              <h1 className="text-2xl font-bold text-[#1e3a5f]">Transaction Log</h1>
            </div>
            <Button variant="outline" onClick={exportCSV} className="text-sm" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">${totalRevenue.toFixed(2)}</p>
              <p className="text-xs text-gray-500">Total Revenue (Paid)</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-[#1e3a5f]">{filtered.length}</p>
              <p className="text-xs text-gray-500">Total Transactions</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{filtered.filter(t => t.payment_status === 'paid').length}</p>
              <p className="text-xs text-gray-500">Completed</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{filtered.filter(t => ['initiated', 'pending'].includes(t.payment_status)).length}</p>
              <p className="text-xs text-gray-500">Pending</p>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by email, load ID, or session ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="transaction-search"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="h-10 border rounded-md px-3 text-sm"
              data-testid="transaction-filter"
            >
              <option value="all">All Types</option>
              <option value="subscription">Subscriptions</option>
              <option value="adhoc_payment">Ad-hoc Payments</option>
              <option value="shipper_payment">Shipper Payments</option>
            </select>
          </div>

          {/* Transaction Table */}
          {loading ? (
            <p className="text-center text-gray-500 py-8">Loading transactions...</p>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#1e3a5f] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">User</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-left">Reference</th>
                    <th className="px-4 py-3 text-left hidden lg:table-cell">Promo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-8 text-gray-500">No transactions found.</td>
                    </tr>
                  ) : (
                    filtered.map((t, i) => (
                      <tr key={t.id || i} className="hover:bg-gray-50" data-testid={`transaction-row-${i}`}>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          {new Date(t.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                          <br />
                          <span className="text-xs text-gray-400">{new Date(t.created_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-gray-900">{getUser(t)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-gray-700">{getTypeLabel(t)}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          <span className={t.amount === 0 ? 'text-gray-400' : 'text-gray-900'}>
                            ${(t.amount || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(t.payment_status)}`}>
                            {t.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {t.load_id && t.load_id !== 'subscription' ? (
                            <a href={`/load/${t.load_id}`} className="text-blue-600 hover:underline text-xs" target="_blank" rel="noopener noreferrer">
                              {t.load_id.slice(0, 8)}
                            </a>
                          ) : (
                            <span className="text-xs text-gray-600">{getReference(t)}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          {t.metadata?.promo_code ? (
                            <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">{t.metadata.promo_code}</span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AdminTransactions;
